import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Zap } from "lucide-react";

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState("Authenticating...");

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from URL fragment
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        console.error("No session_id in URL hash");
        navigate("/", { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];
      setStatus("Verifying session...");

      try {
        // Exchange session_id for session_token
        const response = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error("Auth session exchange failed:", errorData);
          throw new Error(errorData.detail || "Auth failed");
        }

        const data = await response.json();
        
        // Store session token in localStorage BEFORE setting user
        // This ensures the token is available for subsequent API calls
        if (data.session_token) {
          localStorage.setItem("session_token", data.session_token);
        }
        
        // Clear the hash from URL
        window.history.replaceState(null, "", window.location.pathname);
        
        setStatus("Setting up your account...");
        
        // Set user in context
        setUser(data.user);
        
        // Give React time to propagate the state change
        await new Promise(resolve => setTimeout(resolve, 200));

        // Determine redirect destination
        let destination;
        if (!data.user.role) {
          destination = "/role-selection";
        } else if (data.user.role === "recruiter") {
          destination = "/dashboard/recruiter";
        } else {
          destination = "/dashboard/jobseeker";
        }
        
        setStatus("Redirecting...");
        
        // Use window.location for more reliable navigation after OAuth
        window.location.href = destination;
      } catch (error) {
        console.error("Auth error:", error);
        setStatus("Authentication failed. Redirecting...");
        await new Promise(resolve => setTimeout(resolve, 1000));
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-obsidian flex flex-col items-center justify-center">
      <div className="w-16 h-16 bg-crimson flex items-center justify-center mb-6 animate-pulse-glow">
        <Zap className="w-8 h-8 text-white" />
      </div>
      <h2 className="font-heading text-2xl font-bold mb-2">AUTHENTICATING</h2>
      <p className="text-gray-400">{status}</p>
      <div className="mt-8 w-48 h-1 bg-[#222] overflow-hidden">
        <div className="h-full bg-crimson animate-pulse" style={{ width: "60%" }} />
      </div>
    </div>
  );
};

export default AuthCallback;
